/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {AxiosInstance} from 'axios';
import {LoadParams, Service, SortDirection} from '@ion/core/src/types/services';
import {get} from '../environment/environment';
import {error} from '../logger/logger';
import {
  EntityStatus,
  GenericObject,
  MigrationDetails,
  MigrationResults,
  ScheduledReport,
  ScheduledReports,
  SFTPConnection
} from '../types/entities';
import {safeJSONParse} from '../utils/JSONUtils';
import {createAsyncClient} from './AsyncClientManager';
import {client, config, url} from './ConnectUtils';
import {getAuthorizationHeader} from './ReportUtils';
import {SisenseOptions, SisenseRole} from './SisenseUtils';

/**
 * Defines a reference to the configured Reports Axios HTTP client instance.
 */
let _reportsClient: AxiosInstance;

// defines the type definitions for getSisenseIFrameURl method...
export interface GetSisenseIFrameURLType {
  serverInfo: GenericObject;
  hasSessionCreated?: boolean;
  dashboardId: string;
};

// defines the type definitions for switchSisenseRole method...
export interface SwitchSisenseRoleParams {
  accountId: number;
  role: SisenseRole;
};

/*
 * ScheduledReports Searchable Properties, Filters, Pagination Response and Results Types
 */
export enum ScheduledReportsSearchableProperty {
  id = 'id',
  name = 'name',
  status = 'status',
  account = 'account',
  createdOn = 'createdOn',
  lastModifiedOn = 'lastModifiedOn',
  username = 'username'
};

export type ScheduledReportsSort = `${SortDirection}${ScheduledReportsSearchableProperty}`;

export type UserFilter = {
  [ScheduledReportsSearchableProperty.id]?: number | string;
  [ScheduledReportsSearchableProperty.name]?: string;
  [ScheduledReportsSearchableProperty.status]?: EntityStatus;
  [ScheduledReportsSearchableProperty.account]?: number | string;
  [ScheduledReportsSearchableProperty.createdOn]?: Date | string;
  [ScheduledReportsSearchableProperty.lastModifiedOn]?: Date | string;
  [ScheduledReportsSearchableProperty.username]?: string;
};

export interface LoadScheduledReportsParams extends LoadParams {
  name?: string;
  maxResults?: number;
  sortOn?: ScheduledReportsSort | ScheduledReportsSort[];
  filterOn?: string;
  includeGlobalSearch?: boolean;
  recipientEmail?: string;
};

export type LoadScheduledReportsResponse = {
  data: {
    content: ScheduledReports,
    totalElements: number;
    totalPages: number;
  }
};

export type LoadScheduledReportsResult = {
  scheduledReports: ScheduledReports;
  totalCount: number;
  totalPages: number;
};

export interface LoadMigrationResultsParams extends LoadParams {
  name?: string;
  maxResults?: number;
  filterOn?: string;
  includeGlobalSearch?: boolean;
  migrationId?: string
};

export type LoadMigrationResultsResponse = {
  data: {
    content: MigrationResults,
    totalElements: number;
    totalPages: number;
  }
};

export type LoadMigrationDetailResponse = {
  data: {
    content: MigrationDetails,
    totalElements: number;
    totalPages: number;
  }
};

export type LoadMigrationResultsResult = {
  migrationResults: MigrationResults;
  totalCount: number;
  totalPages: number;
};

export type LoadMigrationDetailsResult = {
  migrationDetails: MigrationDetails;
  totalCount: number;
  totalPages: number;
};

/**
 * Defines the session id to be used for authenticating with Reports Services.
 *
 * @SESSION_ID
 * @type {string}
 */
export const SESSION_ID = Math.random().toString(36).slice(2);

/**
 * Parses the connect response JSON document and returns the value.
 *
 * @method  parse
 * @param   {object} response the returned service response to be parsed.
 * @returns {object} The JSON representation of the service response.
 */
export const parse = (response) => {
  return response.text().then((text) => {
    if (text) {
      const resp = safeJSONParse(text);
      return resp?.results || resp;
    }
    return {};
  });
};

/**
 * Returns the default Connect Service request options
 * based on the given environment (i.e. environment.json configuration).
 *
 * @returns {object} an object representing the service request options.
 */
export const OPTIONS = {
  method      : 'GET',
  mode        : 'cors',
  cache       : 'no-cache',
  credentials : 'include',
  headers     : {
    'Content-Type' : 'application/xml'
  }
};

/**
 * @function registerToken
 *
 * Initiates the registration of the Connect session with the reporting
 * platform.
 *
 *  ```typescript
 *    import {registerToken} from '@ion/core/src/services/Reports';
 *
 *    const token = await registerToken();
 *
 * ```
 */
export const registerToken = async() => {
  const url = `${get('reportsServiceURL')}/registertoken/${get('reportsServiceId')}/${SESSION_ID}`;
  const result = await client.get(url);
  return result;
};

/**
 * The getSisenseOptions method provides the second step of initialization of the
 * Sisense platform. This method handles the retrieval of the authenticated
 * users Sisense configuration based on the given account Id with user settings.
 *
 * ```typescript
 *
 *  import {getSisenseOptions} from '@ion/core/src/services/Reports';
 *
 *  const options = await getSisenseOptions(accountId);
 *
 * ```
 */
export const getSisenseOptions = async(accountId, settings: GenericObject = {}) => {
  const url = `${get('reportsServiceURL')}/sisenseOptions/?accountId=${accountId}`;
  const result = await client.post(url, JSON.stringify({settings}), {
    headers : {
      // eslint-disable-next-line quote-props
      'Authorization' : `Bearer ${SESSION_ID}`
    }
  });
  return result;
};

/**
 * The getSisenseSession method provides step for creating theSisense session.
 * in the client side. This method handles the creating the sessions and cookies of
 * the authenticated users Sisense configuration.
 *
 * ```typescript
 *
 *  import {getSisenseSession} from '@ion/core/src/services/Reports';
 *
 *  await getSisenseSession(options);
 *
 * ```
 */
export const getSisenseSession = async({ssoJwtUrl, jwtToken}: SisenseOptions) => {
  const url = `${ssoJwtUrl}?jwt=${jwtToken}`;
  const result = await getAxiosClient().get(url, {
    maxRedirects : 0,
    headers      : {
      'content-type' : 'application/json'
    }
  });
  return result;
};

/**
 * The `getSisenseToken` function provides the 4th, and optional step within the
 * Sisense initialization process. This function is primarily used for APIs which
 * need access to the Bearer Authorization JWT Token.
 *
 * ```typescript
 *
 *  import {getSisenseToken} from '@ion/core/src/services/Reports';
 *
 *  try {
 *    const token = await getSisenseToken();
 *  } catch (err) {
 *    error(`An Exception occurred while retrieving the Sisense Token. ${err}`);
 *  }
 *
 *
 * ```
 */
export const getSisenseToken = async() => {
  const url = `${get('sisenseBaseURL')}/api/v1/authentication/tokens/api`;
  const result = await getAxiosClient().get(url, {
    headers : {
      'content-type' : 'application/json'
    }
  });
  const data = result.data;
  return result?.data?.token;
};

/**
 * The switchSisenseRole function is used to switch the role of a user in Sisense
 * for a specific account. This function sends a request to a Sisense endpoint to
 * change the user's role.
 *
 * ```typescript
 *
 *  import {switchSisenseRole} from '@ion/core/src/services/Reports';
 *
 *  await switchSisenseRole(5099, 'designer');
 *
 * ```
 *
 * @param accountId The unique identifier of the account for which the user's role should be changed.
 * @param role The new role to assign to the user. It can be one of the following roles: 'viewer' or 'designer'.
 * @returns result of the role switch operation
 */
export const switchSisenseRole = async({
  accountId,
  role
}: SwitchSisenseRoleParams) => {
  const url = `${get('reportsServiceURL')}/sisenseOptions/changeUserRole?role=${role}&accountId=${accountId}`;
  const result = await client.post(url, {}, {
    headers : {
      // eslint-disable-next-line quote-props
      'Authorization' : `Bearer ${SESSION_ID}`
    }
  });
  return result;
};

/**
 * The expireSisenseJWT method provides step for logout of the Sisense platform.
 * This method handles the clearing the sessions and cookies of
 * the authenticated users Sisense configuration.
 *
 * ```typescript
 *
 *  import {expireSisenseJWT} from '@ion/core/src/services/Reports';
 *
 *  await expireSisenseJWT(options);
 *
 * ```
 */
export const expireSisenseJWT = async({logoutUrl}) => {
  await client.get(logoutUrl);
};

/**
 * This method is used to make a API call to sisense server with default header
 * for restoring the Sisense dashboard to it's shared state in initial load.
 *
 * ```typescript
 *
 *  import {restoreDashboard} from '@ion/core/src/services/Reports';
 *
 *  await restoreDashboard(dashboardId);
 *
 * ```
 */
export const restoreDashboard = async(dashboardId: string) => {
  try {
    const uri = `${get('sisenseBaseURL')}/api/dashboards/${dashboardId}/restore`;
    await getAxiosClient().get(uri);
  } catch (e) {
    error(`Restore dashboard is failed for dashboard: ${dashboardId} and reason: ${e.message}`);
  }
};

/**
 * This method is used to make a API call to sisense server with default header
 * for expire the session or logout the user. This method itself will handles
 * all exceptions that should not disturb the application.
 *
 *
 * ```typescript
 *
 *  import {logout} from '@ion/core/src/services/Reports';
 *
 *  await logout();
 *
 * ```
 */
export const logout = async() => {
  try {
    await getAxiosClient().get(`${get('sisenseBaseURL')}/api/v1/authentication/logout`);
  } catch (err) {
    error(`Clearing sisense session is failed. Reason: ${err.message}`);
  }
};

/**
 * This method is used to make an API call to the Sisense server to fetch
 * all available dashboard metadata. It returns the full navver structure,
 * which includes folders and dashboards used in the reporting UI.
 *
 * This method internally handles all exceptions silently to ensure it does
 * not interrupt the application flow in case of failure.
 *
 * ```typescript
 * import {getDashboards} from '@ion/core/src/services/Reports';
 *
 * const response = await getDashboards();
 * const dashboards = response?.data;
 * ```
 *
 * @returns A Promise resolving to the Axios response containing dashboard metadata,
 *          or `undefined` in case of an error.
 */
export const getDashboards = async() => {
  try {
    return await getAxiosClient().get(`${get('sisenseBaseURL')}/api/v1/navver`);
  } catch (err) {
    error(`Fetching dashboards failed. Reason: ${err.message}`);
    throw new Error(err);
  }
};

/**
 * Fetches the hierarchical dashboard structure from the Reports service.
 * The response includes the complete folder–dashboard tree used to render
 * the reporting navigation UI.
 *
 * This hierarchy may include:
 * - Root-level dashboards
 * - Nested folders
 * - Dashboards within folders at any depth
 *
 * This method internally handles all exceptions silently to ensure it does
 * not interrupt the application flow in case of failure.
 *
 * ```typescript
 * import {getDashboadHierarchy} from '@ion/core/src/services/Reports';
 *
 * const response = await getDashboadHierarchy();
 * const hierarchy = response?.data;
 * ```
 *
 * @returns A Promise resolving to the Axios response containing the dashboard
 *          hierarchy data, or `undefined` in case of an error.
 */
export const getDashboadHierarchy = async() => {
  try {
    const url = `${get('reportsServiceURL')}/dashboards/hierarchy`;
    const result = await client.get(url, {
      headers : getAuthorizationHeader(config, SESSION_ID)
    });
    return result;
  } catch (err) {
    error(`Fetching dashboards failed. Reason: ${err.message}`);
    throw new Error(err);
  }
};

/**
 * Synchronizes the current application session with the Sisense server
 * using a user API token. This ensures that the Sisense session remains
 * active and authenticated for subsequent dashboard and report requests.
 *
 * This method is typically invoked during application startup or when
 * the Sisense session needs to be refreshed and GGC change.
 *
 * This method internally handles all exceptions silently to ensure it does
 * not interrupt the application flow in case of failure.
 *
 * ```typescript
 * import { syncSisenseSession } from '@ion/core/src/services/Reports';
 *
 * const token = 'eW91ci1zaXNlbmNlLWFwaS10b2tlbi1oZXJl...';
 * await syncSisenseSession(token);
 * ```
 *
 * @param token - A valid Sisense user API token used to authenticate and
 *                synchronize the session.
 *
 * @returns A Promise resolving to the Axios response indicating whether
 *          the session synchronization was successful, or `undefined`
 *          in case of an error.
 */
export const syncSisenseSession = async(token: string) => {
  try {
    const url = `${get('reportsServiceURL')}/sync-session`;
    const result = await client.post(
      url,
      {userApiToken: token},
      {
        headers : getAuthorizationHeader(config, SESSION_ID)
      }
    );
    return result;
  } catch (err) {
    error(`Syncing sisense session failed. Reason: ${err.message}`);
    throw new Error(err);
  }
};

/**
 * The function takes an accountId (number or string) and an api function as parameters.If an error
 * occurs during the API call and the error message includes 'login to that account first',
 * the function will attempt to get Sisense options for the specified account to create the account entry
 * at the Sisense and then retry the API call.
 *
 * ```typescript
 *
 *  import {verifySisenseAccountEntry} from '@ion/core/src/services/Reports';
 *
 *  await verifySisenseAccountEntry(5010, api);
 *
 * ```
 * @param accountId - The ID of the account
 * @param api - The API function to be executed.
 */
export const verifySisenseAccountEntry = async(
  accountId: number | string,
  api: () => any
): Promise<void> => {
  try {
    return await api();
  } catch (error) {
    if (
      error?.response?.data?.message?.includes('login to that account first') ||
      error?.message?.includes('login to that account first')
    ) {
      // Retry the API call after getting Sisense options for the specified account
      await getSisenseOptions(accountId);
      return await api();
    }
    throw new Error(error);
  }
};

/**
 * The getSFTPConfigurations function is an asynchronous function that
 * retrieves SFTP configurations for a given account ID from the meta server.
 *
 * ```typescript
 *
 *  import {getSFTPConfigurations} from '@ion/core/src/services/Reports';
 *
 *  const configurations = await getSFTPConfigurations();
 *
 * ```
 */
export const getSFTPConfigurations = async(accountId?: number | string, v2 = false) => {
  const headers = getAuthorizationHeader(config, SESSION_ID);
  if (v2) {
    const endpoint = '/report-scheduler/file-upload-configs?size=100';
    const createUrl = url(endpoint, Service.Base);
    return client.get(createUrl, {headers});
  }
  return verifySisenseAccountEntry(accountId as number | string, async() => (
    client.get(`${get('reportsServiceURL')}/sisenseOptions/sftpServers?accountId=${accountId}`, {headers})
  ));
};

/**
 * The createSFTPServer function is an asynchronous function that
 * saves the SFTP configurations for a given account ID on the meta server.
 *
 * ```typescript
 *
 *  import {createSFTPServer} from '@ion/core/src/services/Reports';
 *
 *  await createSFTPServer();
 *
 * ```
 */
export const createSFTPServer = async(
  accountIdOrConfig: number | string | SFTPConnection | {},
  configuration?: SFTPConnection | {}
) => {
  const headers = getAuthorizationHeader(config, SESSION_ID);
  if (configuration === undefined) {
    const endpoint = '/report-scheduler/file-upload-configs';
    const createUrl = url(endpoint, Service.Base);
    return client.post(createUrl, JSON.stringify(accountIdOrConfig), {headers});
  }
  return verifySisenseAccountEntry(accountIdOrConfig as number | string, async() => (
    client.post(
      `${get('reportsServiceURL')}/sisenseOptions/sftpServers?accountId=${accountIdOrConfig}`,
      JSON.stringify(configuration),
      {headers}
    )
  ));
};

/**
 * The createSFTPServer function is an asynchronous function that
 * saves the SFTP configurations for a given account ID on the meta server.
 *
 * ```typescript
 *
 *  import {createSFTPServer} from '@ion/core/src/services/Reports';
 *
 *  await createSFTPServer();
 *
 * ```
 */
export const editSFTPServer = async(serverId: number | string, configuration: SFTPConnection | {}) => {
  const endpoint = `/report-scheduler/file-upload-configs/${serverId}`;
  const createUrl = url(endpoint, Service.Base);
  const results = await client.put(createUrl, JSON.stringify(configuration), {
    headers : getAuthorizationHeader(config, SESSION_ID)
  });
  return results;
};

/**
 * The createSFTPServer function is an asynchronous function that
 * saves the SFTP configurations for a given account ID on the meta server.
 *
 * ```typescript
 *
 *  import {createSFTPServer} from '@ion/core/src/services/Reports';
 *
 *  await createSFTPServer();
 *
 * ```
 */
export const testSFTPServer = async(configuration: SFTPConnection | {}) => {
  const endpoint = '/report-scheduler/file-upload-configs/test';
  const createUrl = url(endpoint, Service.Base);
  const results = await client.put(createUrl, JSON.stringify(configuration), {
    headers : getAuthorizationHeader(config, SESSION_ID)
  });
  return results;
};

/**
 * The deleteSFTPServer function is an asynchronous function that
 * removes the SFTP configurations for a given account ID on the meta server.
 *
 * ```typescript
 *
 *  import {deleteSFTPServer} from '@ion/core/src/services/Reports';
 *
 *  await deleteSFTPServer(serverName);
 *
 * ```
 */
export const deleteSFTPServer = async(id: number | string, serverName?: string) => {
  const headers = getAuthorizationHeader(config, SESSION_ID);
  if (serverName === undefined) {
    const endpoint = `/report-scheduler/file-upload-configs/${id}`;
    const createUrl = url(endpoint, Service.Base);
    return client.delete(createUrl, {headers});
  }
  return client.delete(
    `${get('reportsServiceURL')}/sisenseOptions/sftpServers?accountId=${id}&serverName=${serverName}`,
    {headers}
  );
};

/**
 * The getSisenseIFrameURL function construct the url for sisense
 * dashboard based on it's session.
 *
 * ```typescript
 *
 *  import {getSisenseIFrameURL} from '@ion/core/src/services/Reports';
 *
 *  const url = getSisenseIFrameURL({serverInfo, dashboardId:'61ae95d512b1a20037dbb6bd'});
 *
 * ```
 */
export const getSisenseIFrameURL = ({
  serverInfo,
  dashboardId,
  hasSessionCreated = false
}: GetSisenseIFrameURLType) => {
  const {baseReportURL, ssoJwtUrl, reportRequestParameters, jwtToken} = serverInfo;
  let path = `${baseReportURL}${dashboardId}?${reportRequestParameters}`;
  if (!hasSessionCreated) {
    path = `${ssoJwtUrl}?jwt=${jwtToken}&return_to=${encodeURIComponent(path)}`;
  }
  return path;
};

/**
 *
 * The getAxiosClient function is a private function to retrieve a singleton
 * AxiosInstance for reports.
 *
 */
const getAxiosClient = (): AxiosInstance => {
  if (!_reportsClient) {
    _reportsClient = createAsyncClient(null);
  }
  return _reportsClient;
};

/**
 * Encodes a comma-separated keyword list for a query param: each keyword is
 * encoded individually so the commas stay as literal separators.
 */
const encodeSearchList = (value: string): string =>
  value
    .split(',')
    .map((keyword) => encodeURIComponent(keyword.trim()))
    .filter(Boolean)
    .join(',');

/**
 *
 * The getScheduledReports function is used to fetch scheduled reports.
 *
 * ```typescript
 *
 *  import {getScheduledReports} from '@ion/core/src/services/Reports';
 *
 *  const response = await getScheduledReports({accountId, sortOn, name});
 *
 * ```
 */
export const getScheduledReports = async({
  accountId,
  sortOn,
  name,
  pageId = 1,
  pageSize = 20,
  maxResults = 100,
  filterOn = '',
  recipientEmail = '',
  includeSubGroups = false,
  includeGlobalSearch = false
}: LoadScheduledReportsParams): Promise<LoadScheduledReportsResult> => {
  const uri = `/report-scheduler/report-schedules?includeSubaccount=${includeSubGroups}&page=${pageId}&size=${pageSize}`;

  // `search` makes the API ignore every other filter param, so the two search
  // params are mutually exclusive -- never append both.
  let endpoint = uri;
  if (filterOn !== '') {
    endpoint += `&search=${encodeURIComponent(filterOn)}`;
  } else if (recipientEmail !== '') {
    endpoint += `&recipientEmail=${encodeSearchList(recipientEmail)}`;
  }

  const reportsSchedulerUrl = url(endpoint, Service.Base);
  const response: LoadScheduledReportsResponse = await client.get(reportsSchedulerUrl, {
    headers : getAuthorizationHeader(config, SESSION_ID)
  });

  const {content, totalElements, totalPages} = response?.data;

  return {
    scheduledReports : content,
    totalCount       : totalElements,
    totalPages
  };
};

/**
 *
 * The deleteScheduledReport function is used to delete a selected scheduled report.
 *
 * ```typescript
 *
 *  import {deleteScheduledReport} from '@ion/core/src/services/Reports';
 *
 *  await deleteScheduledReport(reportId);
 *
 * ```
 */
export const deleteScheduledReport = async(reportId: number | string) => {
  const endpoint = `/report-scheduler/report-schedules/${reportId}`;
  const deleteUrl = url(endpoint, Service.Base);
  await client.delete(deleteUrl, {
    headers : getAuthorizationHeader(config, SESSION_ID)
  });
};

/**
 *
 * The createScheduledReports function is used to fetch scheduled reports.
 *
 * ```typescript
 *
 *  import {createScheduledReports} from '@ion/core/src/services/Reports';
 *
 *  await createScheduledReports(reportId);
 *
 * ```
 */
export const createScheduledReport = async(scheduledReport: ScheduledReport) => {
  const endpoint = '/report-scheduler/report-schedules';
  const createUrl = url(endpoint, Service.Base);
  await client.post(createUrl, JSON.stringify(scheduledReport), {
    headers : getAuthorizationHeader(config, SESSION_ID)
  });
};

/**
 *
 * The editScheduledReports function is used to fetch scheduled reports.
 *
 * ```typescript
 *
 *  import {editScheduledReports} from '@ion/core/src/services/Reports';
 *
 *  await editScheduledReports(reportId);
 *
 * ```
 */
export const editScheduledReport = async(scheduledReport: ScheduledReport) => {
  const scheduledReportId = scheduledReport?.id;
  delete scheduledReport?.id;
  const endpoint = `/report-scheduler/report-schedules/${scheduledReportId}`;
  const createUrl = url(endpoint, Service.Base);
  const {data} = await client.put(createUrl, JSON.stringify(scheduledReport), {
    headers : getAuthorizationHeader(config, SESSION_ID)
  });
  return data;
};

/**
 * The getMigrationResults function is used to fetch migration results.
 *
 */
export const getMigrationResults = async({
  accountId,
  name,
  pageId = 1,
  pageSize = 20,
  maxResults = 100,
  filterOn = '',
  includeSubGroups = false,
  includeGlobalSearch = false
}: LoadMigrationResultsParams): Promise<LoadMigrationResultsResult> => {
  const uri = `/report-scheduler/migrations/results?includeSubaccount=${includeSubGroups}&page=${pageId}&size=${pageSize}`;

  const migrationsResultsUrl = url(uri, Service.Base);
  const response: LoadMigrationResultsResponse = await client.get(migrationsResultsUrl, {
    headers : getAuthorizationHeader(config, SESSION_ID)
  });

  const {content, totalElements, totalPages} = response?.data;

  return {
    migrationResults : content,
    totalCount       : totalElements,
    totalPages
  };
};

/*
 * The enableScheduledReport function is used to enable a scheduled report.
 *
 * ```typescript
 *
 *  import {enableScheduledReport} from '@ion/core/src/services/Reports';
 *
 *  await enableScheduledReport(reportId);
 *
 * ```
 */
export const enableScheduledReport = async(scheduledReport: ScheduledReport) => {
  const scheduledReportId = scheduledReport?.id;
  delete scheduledReport?.id;
  const endpoint = `/report-scheduler/report-schedules/${scheduledReportId}/enable`;
  const createUrl = url(endpoint, Service.Base);
  await client.put(createUrl, JSON.stringify(scheduledReport), {
    headers : getAuthorizationHeader(config, SESSION_ID)
  });
};

/**
 *
 * The getMigrationResultsById function is used to fetch migration results for each Id.
 *
 */
export const getMigrationResultsById = async({
  migrationId
}: LoadMigrationResultsParams): Promise<LoadMigrationDetailsResult> => {
  const uri = `/report-scheduler/migrations/results/${migrationId}`;

  const migrationsResultsUrl = url(uri, Service.Base);
  const response: LoadMigrationResultsResponse = await client.get(migrationsResultsUrl, {
    headers : getAuthorizationHeader(config, SESSION_ID)
  });

  const {content, totalElements, totalPages} = response?.data;

  return {
    migrationDetails : content,
    totalCount       : totalElements,
    totalPages
  };
};

/**
 * The disableScheduledReport function is used to disable a scheduled report.
 *
 * ```typescript
 *
 *  import {disableScheduledReport} from '@ion/core/src/services/Reports';
 *
 *  await disableScheduledReport(reportId);
 *
 * ```
 */
export const disableScheduledReport = async(scheduledReport: ScheduledReport) => {
  const scheduledReportId = scheduledReport?.id;
  delete scheduledReport?.id;
  const endpoint = `/report-scheduler/report-schedules/${scheduledReportId}/enable`;
  const deleteUrl = url(endpoint, Service.Base);
  await client.delete(deleteUrl, {
    headers : getAuthorizationHeader(config, SESSION_ID)
  });
};

/**
 * The getMigrationStatus function is used to fetch migration status.
 *
 */
export const getMigrationStatus = async({
  accountId,
  name,
  pageId = 1,
  pageSize = 20,
  maxResults = 100,
  filterOn = '',
  includeSubGroups = false,
  includeGlobalSearch = false
}: LoadMigrationResultsParams): Promise<LoadMigrationResultsResult> => {
  const uri = '/report-scheduler/migrations/status?includeSubaccount=true&size=1000';

  const migrationsResultsUrl = url(uri, Service.Base);
  const response: LoadMigrationResultsResponse = await client.get(migrationsResultsUrl, {
    headers : getAuthorizationHeader(config, SESSION_ID)
  });

  const {content, totalElements, totalPages} = response?.data;

  return {
    migrationResults : content,
    totalCount       : totalElements,
    totalPages
  };
};

/**
 * The runNowScheduledReport function is used to run a scheduled report immediately.
 *
 * ```typescript
 *
 *  import {runNowScheduledReport} from '@ion/core/src/services/Reports';
 *
 *  await runNowScheduledReport(reportId);
 *
 * ```
 */
export const runNowScheduledReport = async(scheduledReport: ScheduledReport) => {
  const scheduledReportId = scheduledReport?.id;
  const endpoint = `/report-scheduler/report-schedules/${scheduledReportId}/run-now`;
  const runNowUrl = url(endpoint, Service.Base);
  await client.put(runNowUrl, {}, {
    headers : getAuthorizationHeader(config, SESSION_ID)
  });
};

/**
 *
 * The StartScheduleMigration function is used to fetch scheduled reports.
 *
 * ```typescript
 *
 *  import {StartScheduleMigration} from '@ion/core/src/services/Reports';
 *
 *  await StartScheduleMigration();
 *
 * ```
 */
export const StartScheduleMigration = async(migrateInfo: any) => {
  const endpoint = '/report-scheduler/migrations';
  const createUrl = url(endpoint, Service.Base);
  await client.post(createUrl, JSON.stringify(migrateInfo), {
    headers : getAuthorizationHeader(config, SESSION_ID)
  });
};
